import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Button, Row, Col } from 'react-bootstrap';
import { usePerfil } from '../../../hooks/perfil';
import { useToast } from '../../../hooks/toast';

import Loading from '../../../components/Loading';
import SelectSync, { IOption } from '../../../components/SelectSync';
import TextArea from '../../../components/TextArea';
import getValidationErrors from '../../../utils/getValidationErrors';
import Toggle from '../../../components/Exame/Toggle';
import api from '../../../services/api';
import {
  formataCNPJ,
  formataCep,
  formataTelefone,
  replaceCaracteres,
} from '../../../utils/funcoes';
import getResponseErrors from '../../../utils/getResponseErrors';
import Input from '../../../components/Input';
import InputMask from '../../../components/InputMask';
import apiCep from '../../../services/apiCep';
import { optionsUF } from '../../../utils/options';
import { useAuth } from '../../../hooks/auth';

interface IHorario {
  HorCodigo?: number;
  HorConCodigo?: number;
  HorIntervaloIni: string;
  HorIntervaloFim: string;
  HorHoraIni: string;
  HorHoraFim: string;
  HorMaxAgendamento?: number;
}

interface IHorarioRecorrente extends IHorario {
  HorDataIni: Date | string;
  HorDiasRecorre: number | string;
}

interface IHorarios extends IHorario {
  HorDia: number | string;
  selecionado?: boolean;
}

export interface IClinica {
  CliCodigo: number;
  CliNome: string;
  CliNomeAbreviado: string;
  CliCNPJ: string;
  CliRazaoSocial: string;
  CliIE: string;
  CliSimplesNacional: boolean;
  CliTelefone: string;
  CliTelefone2: string;
  CliEmail: string;
  CliFacebook: string;
  CliInstagram: string;
  CliSite: string;
  CliEndereco: string;
  CliNumero: string;
  CliBairro: string;
  CliCidade: string;
  CliCidadeCodigo: string;
  CliCEP: string;
  CliComplemento: string;
  CliUF: string;
  CliContrato: boolean;
  CliProAnamnese: boolean;
  CliProDocumento: boolean;
  CliProAnexo: boolean;
  CliPagamentoAntecipado: boolean;
  CliCor: string;
  CliCodigoRioSulWeb: string;
  CliAtivo: boolean;
  CliNegociacaoCartao: boolean;
  CliEmiteNotaFiscal: boolean;
  CliValidaDadosNF: boolean;
  CliEmiteNFAutomatica: boolean;
  CliEmiteNFCartao: boolean;
  CliDrSaude: boolean;
  CliRepresentante: string;
  CliLatitude: number;
  CliLongitude: number;
  CliLinkMapa: string;
  CliBloqueioPagamento: boolean;
  CliJuros: number;
  CliMulta: number;
  CliDiasVencimento: string;
  CliDiasCobrarJuros: number;
  CliLembreteVencimento: number;
  CliLembreteVencido5: number;
  CliLembreteVencido10: number;
  CliIdentificaPedidoExame: boolean;
  CliFuncionaSabado: boolean;
  CliAtendeConvenio: boolean;
  CliExibeConvenioMedico: boolean;
  CliPerfilAlteraNomeAssociado: number;
  CliPerfilAcessaProcedimentos: number;
  CliPerfilCancelaCartao: number;
  CliPerfilValorCartao: number;
  CliUsaPainel: boolean;
  CliCobrancaAutomatica: boolean;
}

type Props = {
  cliCodigo?: number;

  addClinica(dadosclinica: IClinica, cliCodigo?: number): void;
};

const ClinicasCadastroPagina: React.FC<Props> = ({ cliCodigo, addClinica }) => {
  const { user } = useAuth();
  const { optionsPerfil, buscaPerfil } = usePerfil();

  const { addToast } = useToast();

  const formRefClinica = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);

  const [clinica, setClinica] = useState<IClinica | undefined>(undefined);
  const [loadingCEP, setLoadingCEP] = useState(false);

  useEffect(() => {
    async function loadPerfis(): Promise<void> {
      await buscaPerfil();
    }
    if (!optionsPerfil || Object.keys(optionsPerfil).length === 0) loadPerfis();
  }, [buscaPerfil, optionsPerfil]);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IClinica>(`clinicas/${cliCodigo}`);

        setClinica({
          ...response.data,
        });

        formRefClinica.current?.setData({
          ...formRefClinica.current?.getData(),
          ...response.data,
          CliTelefone: formataTelefone(response.data.CliTelefone),
          CliTelefone2: formataTelefone(response.data.CliTelefone2),
          CliCNPJ: formataCNPJ(response.data.CliCNPJ),
          CliCEP: formataCep(response.data.CliCEP),
          CliSimplesNacional: response.data.CliSimplesNacional.toString(),
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar dados da clínica',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (cliCodigo) loadDados();
  }, [cliCodigo, addToast]);

  const handleSubmitConfiguracao = useCallback(
    async (dados: IClinica) => {
      try {
        setLoading(true);

        formRefClinica.current?.setErrors({});
        const schema = Yup.object().shape({
          CliNome: Yup.string().required(),
          CliNomeAbreviado: Yup.string().required(),
          CliCNPJ: Yup.string().required(),
          CliRazaoSocial: Yup.string().required(),
          CliIE: Yup.string(),
          CliSimplesNacional: Yup.boolean().required(),
          CliTelefone: Yup.string().required(),
          CliTelefone2: Yup.string(),
          CliEmail: Yup.string().required(),
          CliFacebook: Yup.string().required(),
          CliInstagram: Yup.string().required(),
          CliSite: Yup.string().required(),
          CliEndereco: Yup.string().required(),
          CliNumero: Yup.string().required(),
          CliBairro: Yup.string().required(),
          CliCidade: Yup.string().required(),
          CliCidadeCodigo: Yup.string(),
          CliCEP: Yup.string().required(),
          CliComplemento: Yup.string(),
          CliUF: Yup.string().required(),
          CliContrato: Yup.boolean().required(),
          CliProAnamnese: Yup.boolean().required(),
          CliProDocumento: Yup.boolean().required(),
          CliProAnexo: Yup.boolean().required(),
          CliPagamentoAntecipado: Yup.boolean().required(),
          CliCor: Yup.string().required(),
          CliCodigoRioSulWeb: Yup.string().required(),
          CliAtivo: Yup.boolean().required(),
          CliNegociacaoCartao: Yup.boolean().required(),
          CliEmiteNotaFiscal: Yup.boolean().required(),
          CliValidaDadosNF: Yup.boolean().required(),
          CliEmiteNFAutomatica: Yup.boolean().required(),
          CliEmiteNFCartao: Yup.boolean().required(),
          CliDrSaude: Yup.boolean().required(),
          CliRepresentante: Yup.string(),
          CliLatitude: Yup.number().required(),
          CliLongitude: Yup.number().required(),
          CliLinkMapa: Yup.string().required(),
          CliBloqueioPagamento: Yup.boolean().required(),
          CliJuros: Yup.number().required(),
          CliMulta: Yup.number().required(),
          CliDiasVencimento: Yup.string().required(),
          CliDiasCobrarJuros: Yup.number().required(),
          CliLembreteVencimento: Yup.number().required(),
          CliLembreteVencido5: Yup.number().required(),
          CliLembreteVencido10: Yup.number().required(),
          CliIdentificaPedidoExame: Yup.boolean().required(),
          CliFuncionaSabado: Yup.boolean().required(),
          CliAtendeConvenio: Yup.boolean().required(),
          CliExibeConvenioMedico: Yup.boolean().required(),
          CliPerfilAlteraNomeAssociado: Yup.number().required(),
          CliPerfilAcessaProcedimentos: Yup.number().required(),
          CliPerfilCancelaCartao: Yup.number().required(),
          CliPerfilValorCartao: Yup.number().required(),
          CliUsaPainel: Yup.boolean().required(),
          CliCobrancaAutomatica: Yup.boolean().required(),
        });

        const simplesNacional = dados.CliSimplesNacional
          ? dados.CliSimplesNacional.toString()
          : 'false';
        const contrato = dados.CliContrato
          ? dados.CliContrato.toString()
          : 'false';
        const proAnamnese = dados.CliProAnamnese
          ? dados.CliProAnamnese.toString()
          : 'false';
        const proDocumento = dados.CliProDocumento
          ? dados.CliProDocumento.toString()
          : 'false';
        const proAnexo = dados.CliProAnexo
          ? dados.CliProAnexo.toString()
          : 'false';
        const pagamentoAntecipado = dados.CliPagamentoAntecipado
          ? dados.CliPagamentoAntecipado.toString()
          : 'false';
        const negociacaoCartao = dados.CliNegociacaoCartao
          ? dados.CliNegociacaoCartao.toString()
          : 'false';
        const emiteNotaFiscal = dados.CliEmiteNotaFiscal
          ? dados.CliEmiteNotaFiscal.toString()
          : 'false';
        const validaDadosNF = dados.CliValidaDadosNF
          ? dados.CliValidaDadosNF.toString()
          : 'false';
        const emiteNFAutomatica = dados.CliEmiteNFAutomatica
          ? dados.CliEmiteNFAutomatica.toString()
          : 'false';
        const emiteNFCartao = dados.CliEmiteNFCartao
          ? dados.CliEmiteNFCartao.toString()
          : 'false';
        const drSaude = dados.CliDrSaude
          ? dados.CliDrSaude.toString()
          : 'false';
        const bloqueioPagamento = dados.CliBloqueioPagamento
          ? dados.CliBloqueioPagamento.toString()
          : 'false';
        const identificaPedidoExame = dados.CliIdentificaPedidoExame
          ? dados.CliIdentificaPedidoExame.toString()
          : 'false';

        const funcionaSabado = dados.CliFuncionaSabado
          ? dados.CliFuncionaSabado.toString()
          : 'false';
        const atendeConvenio = dados.CliAtendeConvenio
          ? dados.CliAtendeConvenio.toString()
          : 'false';
        const exibeConvenioMedico = dados.CliExibeConvenioMedico
          ? dados.CliExibeConvenioMedico.toString()
          : 'false';
        const usaPainel = dados.CliUsaPainel
          ? dados.CliUsaPainel.toString()
          : 'false';
        const cobrancaAutomatica = dados.CliCobrancaAutomatica
          ? dados.CliCobrancaAutomatica.toString()
          : 'false';

        const configuracaoForm: IClinica = {
          ...dados,
          CliSimplesNacional: simplesNacional === 'true',
          CliContrato: contrato === 'true',
          CliProAnamnese: proAnamnese === 'true',
          CliProDocumento: proDocumento === 'true',
          CliProAnexo: proAnexo === 'true',
          CliPagamentoAntecipado: pagamentoAntecipado === 'true',
          CliNegociacaoCartao: negociacaoCartao === 'true',
          CliEmiteNotaFiscal: emiteNotaFiscal === 'true',
          CliValidaDadosNF: validaDadosNF === 'true',
          CliEmiteNFAutomatica: emiteNFAutomatica === 'true',
          CliEmiteNFCartao: emiteNFCartao === 'true',
          CliDrSaude: drSaude === 'true',
          CliBloqueioPagamento: bloqueioPagamento === 'true',
          CliIdentificaPedidoExame: identificaPedidoExame === 'true',
          CliFuncionaSabado: funcionaSabado === 'true',
          CliAtendeConvenio: atendeConvenio === 'true',
          CliExibeConvenioMedico: exibeConvenioMedico === 'true',
          CliUsaPainel: usaPainel === 'true',
          CliCobrancaAutomatica: cobrancaAutomatica === 'true',
          CliTelefone: replaceCaracteres(dados.CliTelefone),
          CliTelefone2: replaceCaracteres(dados.CliTelefone2),
          CliCEP: replaceCaracteres(dados.CliCEP),
          CliCNPJ: replaceCaracteres(dados.CliCNPJ),
          CliNome: dados.CliNome.toUpperCase(),
          CliNomeAbreviado: dados.CliNomeAbreviado.toUpperCase(),
          CliRazaoSocial: dados.CliRazaoSocial.toUpperCase(),
          // ConObs: dados.ConObs.toUpperCase(),
        };

        console.log(
          await schema.validate(configuracaoForm, {
            abortEarly: false,
          }),
        );

        await schema.validate(configuracaoForm, {
          abortEarly: false,
        });

        const dadosEnvio: IClinica = {
          ...configuracaoForm,
        };

        await addClinica(dadosEnvio, cliCodigo);
      } catch (error) {
        console.log(error);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefClinica.current?.setErrors(errors);
          return;
        }
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Erro ao salvar agenda',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, addClinica, cliCodigo],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function, prettier/prettier
  const handleOnChangeVazio = useCallback(() => { }, []);

  const diasAviso = useMemo((): IOption[] => {
    const horas = [];
    // eslint-disable-next-line no-plusplus
    for (let h = 1; h < 7; h++) {
      horas.push({ label: `${h}`, value: `${h}` });
    }
    return horas;
  }, []);

  const handleBuscaCepApi = useCallback(async () => {
    if (formRefClinica && formRefClinica.current) {
      const cep = replaceCaracteres(
        String(formRefClinica.current.getFieldValue('CliCEP')),
      );

      if (cep && cep.length !== 8) {
        return;
      }

      setLoadingCEP(true);
      try {
        const response = await apiCep.get(`/${cep}/json`);
        formRefClinica.current?.setData({
          ...formRefClinica.current?.getData(),
          CliBairro: response.data.bairro,
          CliCidade: response.data.localidade,
          CliCidadeCodigo: response.data.ibge,
          CliEndereco: response.data.logradouro,
          CliUF: response.data.uf,
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível consultar o cep, confira seus dados',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoadingCEP(false);
      }
    }
  }, [addToast]);

  return (
    <div className="container">
      {loading && <Loading />}

      <Row noGutters>
        <Col md={12}>
          {clinica && (
            <>
              <Form
                ref={formRefClinica}
                initialData={{ ...clinica }}
                onSubmit={handleSubmitConfiguracao}
              >
                <Row>
                  <Col md={6} className="form-group pr-2">
                    <Input
                      name="CliNome"
                      label="Clínica"
                      placeholder="Nome da Clínica"
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                  <Col md={6} className="form-group pr-2">
                    <Input
                      name="CliNomeAbreviado"
                      label="Nome Abreviado"
                      placeholder="Nome abreviado da Clínica"
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6} className="form-group pr-2">
                    <Input
                      name="CliRazaoSocial"
                      label="Razão Social"
                      placeholder="Razao Social da Clínica"
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                  <Col md={6} className="form-group pr-2">
                    <Input
                      name="CliCNPJ"
                      label="CNPJ"
                      placeholder="CNPJ da Clínica"
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={4} className="form-group pr-2">
                    <Input
                      name="CliIE"
                      label="Isnc. Estadual"
                      placeholder="Inscr. Est. da Clínica"
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                  <Col md={4} className="form-group pr-2">
                    <Input
                      name="CliTelefone"
                      label="Telefone"
                      placeholder="Telefone da Clínica"
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                  <Col md={4} className="form-group pr-2">
                    <Input
                      name="CliTelefone2"
                      label="Telefone2"
                      placeholder="Telefone2 da Clínica"
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6} className="form-group pr-2">
                    <Input
                      name="CliEmail"
                      label="E-mail"
                      placeholder="E-mail da Clínica"
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                  <Col md={6} className="form-group pr-2">
                    <Input
                      name="CliSite"
                      label="Site"
                      placeholder="Site da Clínica"
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6} className="form-group pr-2">
                    <Input
                      name="CliFacebook"
                      label="Facebook"
                      placeholder="Facebook da Clínica"
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                  <Col md={6} className="form-group pr-2">
                    <Input
                      name="CliInstagram"
                      label="Instagram"
                      placeholder="Instagram da Clínica"
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                </Row>
                <h4 className="text-muted border-bottom mt-2">Endereço</h4>

                <Row>
                  <Col md={3} lg={2}>
                    <InputMask
                      name="CliCEP"
                      label="CEP"
                      mask="99999-999"
                      placeholder="00000-000"
                      onBlur={handleBuscaCepApi}
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                  <Col md={4}>
                    <br />
                    <Button
                      size="sm"
                      className="mt-1"
                      onClick={handleBuscaCepApi}
                      disabled={user.UsuCodigo !== 1}
                    >
                      {loadingCEP ? 'buscando...' : 'Buscar Endereço'}
                    </Button>
                  </Col>
                  <div className="w-100 mt-2" />
                  <Col md={7}>
                    <Input
                      name="CliEndereco"
                      maxLength={200}
                      label="Endereço"
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                  <Col md={2}>
                    <Input
                      name="CliNumero"
                      maxLength={10}
                      label="Numero"
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                  <Col md={3}>
                    <Input
                      name="CliComplemento"
                      maxLength={30}
                      label="Complemento"
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                </Row>

                <Row className="mt-2">
                  <Col md={4}>
                    <Input
                      name="CliBairro"
                      maxLength={30}
                      label="Bairro"
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                  <Col md={3}>
                    <Input
                      name="CliCidade"
                      maxLength={30}
                      label="Cidade"
                      disabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                  <Col md={2}>
                    <Input
                      name="CliCidadeCodigo"
                      maxLength={30}
                      disabled
                      label="Cidade Codigo"
                    />
                  </Col>
                  <Col md={3}>
                    <SelectSync
                      name="CliUF"
                      label="UF"
                      placeholder="Selecione"
                      handleSelectChange={handleOnChangeVazio}
                      options={optionsUF}
                      isDisabled={user.UsuCodigo !== 1}
                    />
                  </Col>
                </Row>
                <div className={`${user.UsuCodigo !== 1 ? 'd-none' : ''}`}>
                  <Row>
                    <Col md={4} className="form-group pr-2">
                      <Input
                        name="CliRepresentante"
                        label="Representante"
                        placeholder="Representante da Clínica"
                      />
                    </Col>
                    <Col md={4} className="form-group pr-2">
                      <Input
                        name="CliCor"
                        label="Cor"
                        placeholder="Cor da Clínica"
                      />
                    </Col>
                    <Col md={4} className="form-group pr-2">
                      <Input
                        name="CliCodigoRioSulWeb"
                        label="Cod. RioSulWeb"
                        placeholder="CodigoRioSulWeb da Clínica"
                      />
                    </Col>
                  </Row>
                  {/* <Row>
                  <Col md={12} className="form-group pr-2">
                    <SelectSync
                      name="codsPerfil"
                      label="Perfil"
                      placeholder="Selecione"
                      handleSelectChange={handleOnChangeVazio}
                      isMulti
                      options={optionsPerfis}
                      size="gr"
                    />
                  </Col>
                </Row> */}

                  <Row className="pt-2">
                    <Col md={12} lg={12} className="form-group pr-2">
                      <TextArea name="CliLinkMapa" label="Link Mapa" rows={4} />
                    </Col>
                  </Row>
                  <Row className="pt-2">
                    <Col
                      md={3}
                      lg={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliSimplesNacional"
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                        label="Simples Nacional"
                        isDisabled={false}
                      />
                    </Col>

                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliContrato"
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                        label="Contrato"
                        isDisabled={false}
                      />
                    </Col>

                    <Col
                      md={2}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliAtivo"
                        label="Ativo"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>

                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliProAnamnese"
                        label="Anamnese"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>
                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliProDocumento"
                        label="Documento"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>
                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliProAnexo"
                        label="Anexo"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>

                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliPagamentoAntecipado"
                        label="Pagto Antecipado"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>
                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliNegociacaoCartao"
                        label="Negociacao Cartão"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>
                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliEmiteNotaFiscal"
                        label="Emite NF"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>
                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliValidaDadosNF"
                        label="Valida Dados NF"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>
                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliEmiteNFAutomatica"
                        label="Emite NF Automatica"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>
                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliEmiteNFCartao"
                        label="Emite NF Cartao"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>
                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliDrSaude"
                        label="Dr Saude"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>
                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliBloqueioPagamento"
                        label="Bloqueia Pagamento"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>
                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliIdentificaPedidoExame"
                        label="Identifica Ped. Exame"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>

                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliFuncionaSabado"
                        label="Funciona Sabado"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>
                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliAtendeConvenio"
                        label="Atende Convenio"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>
                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliExibeConvenioMedico"
                        label="Exibe Convenio Medico"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>

                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliCobrancaAutomatica"
                        label="Cobranca Automatica"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>
                    <Col
                      md={3}
                      className="form-group pr-2"
                      style={{ display: 'grid', textAlign: 'center' }}
                    >
                      <Toggle
                        nome="CliUsaPainel"
                        label="Usa Painel"
                        isDisabled={false}
                        classSpam={{
                          paddingRight: '10px',
                          marginBottom: '7px',
                          fontWeight: 'bold',
                        }}
                      />
                    </Col>
                  </Row>
                  <Row className="pt-2">
                    <Col
                      md={3}
                      lg={3}
                      className="form-group pr-2"
                      style={{ display: 'grid' }}
                    >
                      <Input name="CliLatitude" label="Latitude" />
                    </Col>
                    <Col
                      md={3}
                      lg={3}
                      className="form-group pr-2"
                      style={{ display: 'grid' }}
                    >
                      <Input name="CliLongitude" label="Longitude" />
                    </Col>
                    <Col
                      md={3}
                      lg={3}
                      className="form-group pr-2"
                      style={{ display: 'grid' }}
                    >
                      <Input name="CliJuros" label="Juros" />
                    </Col>
                    <Col
                      md={3}
                      lg={3}
                      className="form-group pr-2"
                      style={{ display: 'grid' }}
                    >
                      <Input name="CliMulta" label="Multa" />
                    </Col>
                    <Col
                      md={3}
                      lg={3}
                      className="form-group pr-2"
                      style={{ display: 'grid' }}
                    >
                      <Input name="CliDiasCobrarJuros" label="Juros após" />
                    </Col>
                    <Col
                      md={3}
                      lg={3}
                      className="form-group pr-2"
                      style={{ display: 'grid' }}
                    >
                      <Input name="CliDiasVencimento" label="Dias Vencimento" />
                    </Col>
                    <Col
                      md={3}
                      lg={3}
                      className="form-group pr-2"
                      style={{ display: 'grid' }}
                    >
                      <Input
                        name="CliLembreteVencimento"
                        label="Lembrete Venc."
                      />
                    </Col>
                    <Col
                      md={3}
                      lg={3}
                      className="form-group pr-2"
                      style={{ display: 'grid' }}
                    >
                      <Input name="CliLembreteVencido5" label="Lemb Venc 5" />
                    </Col>
                    <Col
                      md={3}
                      lg={3}
                      className="form-group pr-2"
                      style={{ display: 'grid' }}
                    >
                      <Input name="CliLembreteVencido10" label="Lemb Venc 10" />
                    </Col>
                    <Col
                      md={3}
                      lg={3}
                      className="form-group pr-2"
                      style={{ display: 'grid' }}
                    >
                      <Input
                        name="CliPerfilAlteraNomeAssociado"
                        label="Perfil Alt Nome Associado"
                      />
                    </Col>
                    <Col
                      md={3}
                      lg={3}
                      className="form-group pr-2"
                      style={{ display: 'grid' }}
                    >
                      <Input
                        name="CliPerfilAcessaProcedimentos"
                        label="Perfil Acessa Procedtos"
                      />
                    </Col>
                    <Col
                      md={3}
                      lg={3}
                      className="form-group pr-2"
                      style={{ display: 'grid' }}
                    >
                      <Input
                        name="CliPerfilCancelaCartao"
                        label="Perfil Cancela Cartao"
                      />
                    </Col>
                    <Col
                      md={3}
                      lg={3}
                      className="form-group pr-2"
                      style={{ display: 'grid' }}
                    >
                      <Input
                        name="CliPerfilValorCartao"
                        label="Perfil Valor Cartao"
                      />
                    </Col>
                    <Col md={12}>
                      <div className="text-right align-items-center p-1">
                        <>
                          <Button type="submit" variant="success">
                            Salvar dados
                          </Button>
                        </>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Form>
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ClinicasCadastroPagina;
